import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Fade, Typography } from '@mui/material';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import ViewListHeader from 'Components/ViewListHeader';
import { DealerPortalMenuProductTypeEnumValue } from 'enums/DealerPortalMenuProductTypeEnum';
import DealerPortalMenuInventoryItemCard from './DealerPortalMenuInventoryItemCard';
import DealerPortalMenuToolingCard from './DealerPortalMenuToolingCard';
import DealerPortalMenuPanelTypeCard from './DealerPortalMenuPanelTypeCard';
import DealerPortalMenuExteriorSignageCard from './DealerPortalMenuExteriorSignageCard';
import DealerPortalMenuMerchandisingProgramCard from './DealerPortalMenuMerchandisingProgramCard';
import UsefulLinkCard from '../../UsefulLink/Components/UsefulLinkCard';

const DealerPortalMenuProductResultsPanel = ({
  tableDataParams,
  setTableDataParams,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  isFiltering,
  loadMoreAnchorRef,
  sortOptions,
  activeSearchFilter,
  onClearSearchFilter,
  filterLabel,
  clearLabel,
  noItemsLabel,
  loadingMoreLabel,
  displayedRangeLabel,
  filteredSortedProducts,
  buildMenuProductDetailRoute,
  onNavigateToRoute,
  onAddInventoryItemToCart,
  onAddToolingToCart,
}) => {
  const transitionDuration = 240;
  const minimumFilterTransitionMs = 500;
  const placeholderCount = 4;
  const placeholderHeight = 680;
  const [showFilteringTransition, setShowFilteringTransition] = useState(false);
  const filteringStartedAtRef = useRef(0);
  const hideFilteringTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (hideFilteringTimerRef.current) {
        clearTimeout(hideFilteringTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isFiltering) {
      if (hideFilteringTimerRef.current) {
        clearTimeout(hideFilteringTimerRef.current);
        hideFilteringTimerRef.current = null;
      }

      filteringStartedAtRef.current = Date.now();
      setShowFilteringTransition(true);
      return;
    }

    if (!showFilteringTransition) {
      return;
    }

    const elapsed = Date.now() - filteringStartedAtRef.current;
    const remaining = Math.max(0, minimumFilterTransitionMs - elapsed);

    hideFilteringTimerRef.current = setTimeout(() => {
      setShowFilteringTransition(false);
      hideFilteringTimerRef.current = null;
    }, remaining);
  }, [isFiltering, minimumFilterTransitionMs, showFilteringTransition]);

  return (
    <Box sx={{ flex: 1, width: '100%' }}>
      <ViewListHeader
        tableDataParams={tableDataParams}
        setTableDataParams={setTableDataParams}
        isLoading={isLoading}
        sortOptions={sortOptions}
        displayedRangeLabel={displayedRangeLabel}
      />

      {activeSearchFilter ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            mt: 1,
          }}>
          <Typography variant="body2" color="text.secondary">
            {filterLabel}: &quot;{activeSearchFilter}&quot;
          </Typography>
          <Button size="small" onClick={onClearSearchFilter}>
            {clearLabel}
          </Button>
        </Box>
      ) : null}

      <Box sx={{ position: 'relative' }}>
        <Fade in={!showFilteringTransition || isLoading} timeout={transitionDuration} appear={false}>
          <Box>
            <GridContainer
              sx={{
                mt: 1,
                p: 0,
              }}>
              {isLoading
                ? [1, 2, 3, 4].map(index => (
                    <GridItem
                      key={index}
                      xs={12}
                      sm={6}
                      xl={3}
                      lg={3}
                      md={4}
                      style={{ minHeight: `${placeholderHeight}px`, height: `${placeholderHeight}px` }}>
                      <SkeletonWrapper
                        variant="rectangular"
                        fullWidth
                        skeletonProps={{
                          height: '440px',
                          sx: { borderRadius: 0 },
                        }}
                        isLoading
                      />
                    </GridItem>
                  ))
                : filteredSortedProducts.map(product => {
                    const detailRoute = buildMenuProductDetailRoute(product.productId);
                    const onClick = detailRoute ? () => onNavigateToRoute(detailRoute) : undefined;

                    if (product.productType === DealerPortalMenuProductTypeEnumValue.InventoryItem) {
                      return (
                        <GridItem xs={12} sm={6} xl={3} lg={3} md={4} key={product.id}>
                          <DealerPortalMenuInventoryItemCard
                            product={product}
                            view="card"
                            onClick={onClick}
                            onAddToCart={event => {
                              event.stopPropagation();
                              onAddInventoryItemToCart(product);
                            }}
                          />
                        </GridItem>
                      );
                    }

                    if (product.productType === DealerPortalMenuProductTypeEnumValue.Tooling) {
                      return (
                        <GridItem xs={12} sm={6} xl={3} lg={3} md={4} key={product.id}>
                          <DealerPortalMenuToolingCard
                            product={product}
                            view="card"
                            onClick={onClick}
                            onPrimaryAction={event => {
                              event.stopPropagation();
                              if (product.hasAdditionalToolings) {
                                onNavigateToRoute(detailRoute);
                                return;
                              }
                              onAddToolingToCart(product);
                            }}
                          />
                        </GridItem>
                      );
                    }

                    if (product.productType === DealerPortalMenuProductTypeEnumValue.PanelType) {
                      return (
                        <GridItem xs={12} sm={6} xl={3} lg={3} md={4} key={product.id}>
                          <DealerPortalMenuPanelTypeCard
                            product={product}
                            view="card"
                            onClick={onClick}
                            onSetUp={event => {
                              event.stopPropagation();
                              onNavigateToRoute(detailRoute);
                            }}
                          />
                        </GridItem>
                      );
                    }

                    if (product.productType === DealerPortalMenuProductTypeEnumValue.ExteriorSignage) {
                      return (
                        <GridItem xs={12} sm={6} xl={3} lg={3} md={4} key={product.id}>
                          <DealerPortalMenuExteriorSignageCard
                            product={product}
                            view="card"
                            onClick={onClick}
                            onPrimaryAction={event => {
                              event.stopPropagation();
                              onNavigateToRoute(detailRoute);
                            }}
                          />
                        </GridItem>
                      );
                    }

                    if (product.productType === DealerPortalMenuProductTypeEnumValue.MerchandisingProgram) {
                      return (
                        <GridItem xs={12} sm={6} xl={3} lg={3} md={4} key={product.id}>
                          <DealerPortalMenuMerchandisingProgramCard
                            product={product}
                            view="card"
                            onClick={onClick}
                            onPrimaryAction={event => {
                              event.stopPropagation();
                              onNavigateToRoute(detailRoute);
                            }}
                          />
                        </GridItem>
                      );
                    }

                    if (product.productType === DealerPortalMenuProductTypeEnumValue.UsefulLink) {
                      const usefulLink = product.usefulLink || {
                        id: product.productId,
                        name: product.name,
                        description: product.description,
                        warning: product.shortDescription,
                        websiteUrl: '',
                        pdfTitleUrl: '',
                        pdfUrl: '',
                      };

                      return (
                        <GridItem xs={12} sm={6} xl={3} lg={3} md={4} key={product.id}>
                          <UsefulLinkCard key={product.id} usefulLink={usefulLink} />
                        </GridItem>
                      );
                    }

                    return null;
                  })}

              {!isLoading && filteredSortedProducts.length === 0 ? (
                <GridItem xs={12}>
                  <Typography variant="body1" color="textSecondary" sx={{ py: 3, textAlign: 'center' }}>
                    {noItemsLabel}
                  </Typography>
                </GridItem>
              ) : null}

              {!isLoading && hasNextPage ? (
                <GridItem xs={12}>
                  <Box ref={loadMoreAnchorRef} sx={{ width: '100%', height: 1 }} />
                </GridItem>
              ) : null}

              {!isLoading && isFetchingNextPage ? (
                <GridItem xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {loadingMoreLabel}
                  </Typography>
                </GridItem>
              ) : null}
            </GridContainer>
          </Box>
        </Fade>

        <Fade in={Boolean(showFilteringTransition)} timeout={transitionDuration} unmountOnExit>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}>
            <GridContainer sx={{ mt: 1, p: 0 }}>
              {Array.from({ length: placeholderCount }).map((_, index) => (
                <GridItem
                  key={`filtering-overlay-${index}`}
                  xs={12}
                  sm={6}
                  xl={3}
                  lg={3}
                  md={4}
                  style={{ minHeight: `${placeholderHeight}px`, height: `${placeholderHeight}px` }}>
                  <SkeletonWrapper
                    variant="rectangular"
                    fullWidth
                    skeletonProps={{
                      height: '440px',
                      animation: 'wave',
                      sx: { borderRadius: 0 },
                    }}
                    isLoading
                  />
                </GridItem>
              ))}
            </GridContainer>
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default DealerPortalMenuProductResultsPanel;
